import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LiveCount } from "./LiveCount";
import { SiteSearch } from "./SiteSearch";
import { useContent } from "@/context/ContentContext";
import { useText } from "@/lib/useText";

const NAV_IDS = [
  { to: "/", key: "nav_home", id: "inicio" },
  { to: "/novidades", key: "nav_news", id: "novidades" },
  { to: "/regras", key: "nav_rules", id: "regras" },
  { to: "/informacoes", key: "nav_info", id: "informacoes" },
  { to: "/precario", key: "nav_pricing", id: "precario" },
  { to: "/loja", key: "nav_store", id: "loja" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const { content, preview } = useContent();
  const t = useText();
  const NAV = NAV_IDS.map((n) => ({ ...n, label: t(n.key) }));
  const location = useLocation();
  const suffix = preview ? "?preview=1" : "";

  const linkClass = ({ isActive }) =>
    `relative py-2 text-sm font-medium transition-colors duration-200 hover:text-eden-text ${isActive ? "text-eden-text after:absolute after:-bottom-[21px] after:left-0 after:h-px after:w-full after:bg-eden-orange" : "text-eden-secondary"}`;

  return (
    <header className="sticky top-0 z-50 border-b border-eden-border bg-eden-bg/90 backdrop-blur-md" data-testid="site-header">
      {preview && (
        <div className="bg-eden-orange px-4 py-1.5 text-center font-mono text-xs uppercase tracking-widest text-eden-bg" data-testid="preview-banner">
          Pré-visualização do rascunho (não publicado)
        </div>
      )}
      <nav className="container-eden flex h-16 items-center justify-between" aria-label="Navegação principal">
        <Link to={`/${suffix}`} className="flex items-center" data-testid="nav-logo-link" aria-label="EDEN RP, página inicial">
          <Logo url={content?.media?.logo_url} />
        </Link>
        <ul className="hidden items-center gap-6 lg:flex xl:gap-8">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink to={`${n.to}${suffix}`} end={n.to === "/"} className={linkClass} data-testid={`nav-link-${n.id}`}>
                {n.label}
              </NavLink>
            </li>
          ))}
          <li className="ml-2 flex items-center gap-2"><SiteSearch /><LiveCount joinUrl={content?.links?.fivem_join} /></li>
        </ul>
        <div className="flex items-center gap-2 lg:hidden">
          <SiteSearch />
          <LiveCount joinUrl={content?.links?.fivem_join} />
          <button
            type="button"
            className="btn-ghost -mr-2"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Fechar menu" : "Abrir menu"}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
      {open && (
        <div id="mobile-menu" className="border-t border-eden-border bg-eden-bg lg:hidden" data-testid="mobile-menu">
          <ul className="container-eden flex flex-col py-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink
                  to={`${n.to}${suffix}`}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `block border-l-2 py-3 pl-4 text-base font-medium ${isActive ? "border-eden-orange text-eden-text" : "border-transparent text-eden-secondary"}`}
                  data-testid={`mobile-nav-link-${n.id}`}
                  aria-current={location.pathname === n.to ? "page" : undefined}
                >
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
